// src/pages/profesor/ProfesorCuentaPage.tsx
import {
  Stack,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Box,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../../api/axios';
import { useProfesorPerfil } from '../../hooks/useProfesorFicha';
import gorillaprofile from '../../imagenes/Gorillaavatar.png';
import { colorForCentro } from '../../pages/admin/manager/disponibilidad/colorForCentro';
import moralejaImg from '../../imagenes/Centros/moraleja.png';
import plazacastillaImg from '../../imagenes/Centros/plazacastilla.png';
import pozueloImg from '../../imagenes/Centros/pozuelo.png';
import otherImg from '../../imagenes/Centros/other.png';
import { SAD_FEATURE_ENABLED } from '../../config/features';

// Selector de vigencia: usamos la disponibilidad semanal con vigencias
import { useDisponibilidad, type DisponibilidadFilaRead } from '../../hooks/useDisponibilidad';

// Botón de ticket de cambio de disponibilidad
import AvailabilityChangeButton from '../../components/TicketButtons/AvailabilityChangeButton';

export default function ProfesorCuentaPage() {
  const { t } = useTranslation<'translation'>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: perfil, loading, error } = useProfesorPerfil(user?.id);

  // Para representar los centros
  const centerIconMap: Record<number, string> = {
    1: moralejaImg,
    2: plazacastillaImg,
    3: pozueloImg,
    4: otherImg,
  };

  // ——— Disponibilidades (todas las vigencias) y selector de vigencia
  const {
    rows: dispoRows,
    loading: loadingDispo,
    refresh: refreshDispo,
  } = useDisponibilidad(user?.id ?? 0);
  const [vigenciaKey, setVigenciaKey] = useState<string>('base');

  // —— Estado “acepta SAD” (switch)
  const [acceptingSad, setAcceptingSad] = useState<boolean>(true);
  const [loadingAcceptingSad, setLoadingAcceptingSad] = useState<boolean>(false);
  const [savingAcceptingSad, setSavingAcceptingSad] = useState<boolean>(false);

  const fetchAccepting = useCallback(async () => {
    if (!SAD_FEATURE_ENABLED) return;
    if (!user?.id) return;
    try {
      setLoadingAcceptingSad(true);
      const { data } = await api.get<{ accepting: boolean }>('/sad/accepting/mine');
      setAcceptingSad(!!data?.accepting);
    } catch {
      // El interceptor ya muestra error si aplica.
    } finally {
      setLoadingAcceptingSad(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (SAD_FEATURE_ENABLED) fetchAccepting();
  }, [fetchAccepting]);

  // Refresco de disponibilidades cuando hay usuario
  useEffect(() => {
    if (user?.id) refreshDispo();
  }, [user?.id, refreshDispo]);

  /* ========== Utilidades para selector/render de disponibilidad (TODOS los hooks antes de returns) ========== */

  // clave de vigencia ("base" | "YYYY-MM-DD__YYYY-MM-DD|∞")
  const keyForRow = (r: DisponibilidadFilaRead): string => {
    const vd = r.vigenciaDesde ?? '1970-01-01';
    const vh = r.vigenciaHasta ?? null;
    if (vd === '1970-01-01' && (vh == null || vh === '')) return 'base';
    return `${vd}__${vh ?? '∞'}`;
  };

  const labelForKey = (key: string): string => {
    if (key === 'base') return 'Base';
    const [vd, vh] = key.split('__');
    return `${vd} – ${vh === '∞' ? '∞' : vh}`;
  };

  // Agrupar por vigencia
  const gruposPorVigencia = useMemo(() => {
    const map = new Map<string, DisponibilidadFilaRead[]>();
    for (const r of dispoRows) {
      const k = keyForRow(r);
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(r);
    }
    // ordenar cada grupo por día/hora
    for (const [k, arr] of map.entries()) {
      arr.sort(
        (a, b) =>
          a.diaSemana - b.diaSemana ||
          a.horaInicio.localeCompare(b.horaInicio) ||
          a.horaFin.localeCompare(b.horaFin),
      );
      map.set(k, arr);
    }
    return map;
  }, [dispoRows]);

  // Establecer key por defecto cuando cargan datos
  useEffect(() => {
    if (gruposPorVigencia.size) {
      if (gruposPorVigencia.has('base')) setVigenciaKey('base');
      else setVigenciaKey(Array.from(gruposPorVigencia.keys())[0]);
    }
  }, [gruposPorVigencia]);

  const selectedRows = useMemo(
    () => gruposPorVigencia.get(vigenciaKey) ?? [],
    [gruposPorVigencia, vigenciaKey],
  );

  // Mapa idCentro -> nombre (a partir de perfil.centros; tolerante a undefined)
  const perfilCentros = perfil?.centros ?? [];
  const centroNombreById = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of perfilCentros) {
      const id: number | undefined =
        (c as any)?.centro?.id ?? (c as any)?.idCentro ?? (c as any)?.id ?? undefined;
      const nombre: string | undefined =
        (c as any)?.centro?.nombre ?? (c as any)?.nombre ?? (c as any)?.centroNombre ?? undefined;
      if (typeof id === 'number' && nombre) m.set(id, nombre);
    }
    return m;
  }, [perfilCentros]);

  // formatosMask -> nombres (si es 7 => vacío; solo mostramos excepciones)
  const maskToFormatos = (mask?: number): string => {
    const m = typeof mask === 'number' ? mask : 7;
    if (m === 7) return ''; // todos => no mostrar
    const out: string[] = [];
    if (m & 1) out.push('Presencial');
    if (m & 2) out.push('Online');
    if (m & 4) out.push('A domicilio');
    return out.join(' + ');
  };

  // util time
  const parseHoras = (hhmmss: string) => {
    const [h, m] = hhmmss.split(':').map((x) => parseInt(x, 10));
    return h + (m || 0) / 60;
  };
  const hhmm = (hhmmss: string) => hhmmss.slice(0, 5);

  // Construcción del mosaico por día con los rows seleccionados
  type Bloque = { horaInicio: string; horaFin: string; etiqueta: string; durHoras: number };
  const diasMap = useMemo(() => {
    const map: Record<string, Bloque[]> = {};
    const letras = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
    for (const r of selectedRows) {
      const dia = letras[r.diaSemana] ?? String(r.diaSemana);
      if (!map[dia]) map[dia] = [];
      const etiquetaFormatos = maskToFormatos(r.formatosMask);
      const centro =
        r.idCentro != null ? (centroNombreById.get(r.idCentro) ?? `Centro #${r.idCentro}`) : null;

      // Construimos partes sin separadores sobrantes
      const partes: string[] = [`${hhmm(r.horaInicio)} - ${hhmm(r.horaFin)}`];
      if (etiquetaFormatos) partes.push(etiquetaFormatos);
      if (centro != null) partes.push(centro);
      const etiqueta = partes.join(' · ');

      const dur = parseHoras(r.horaFin) - parseHoras(r.horaInicio);
      map[dia].push({ horaInicio: r.horaInicio, horaFin: r.horaFin, etiqueta, durHoras: dur });
    }
    // ordenar bloques por hora dentro de cada día
    Object.values(map).forEach((arr) =>
      arr.sort((a, b) => a.horaInicio.localeCompare(b.horaInicio)),
    );
    return map;
  }, [selectedRows, centroNombreById]);

  // duración máxima para normalizar anchuras
  const durMax = useMemo(() => {
    const durs: number[] = [];
    Object.values(diasMap).forEach((arr) => arr.forEach((b) => durs.push(b.durHoras)));
    return durs.length ? Math.max(...durs) : 1;
  }, [diasMap]);

  /* ================= returns condicionales DESPUÉS de todos los hooks ================= */

  if (!user) return null;

  if (loading) return <CircularProgress sx={{ mt: 4 }} />;
  if (error || !perfil) {
    return (
      <Alert severity="error" sx={{ mt: 4 }}>
        {t('profesorCuenta.errorCarga')}
        {error instanceof Error ? `: ${error.message}` : null}
      </Alert>
    );
  }

  /* datos personales */
  const nombre = perfil.usuario.nombreCompleto ?? '—';
  const telefono = perfil.basicos.telefono ?? '—';
  const fechaCumple = perfil.basicos.fechaCumpleanos ?? '—';
  const fechaAlta = perfil.basicos.fechaIncorporacion ?? '—';

  return (
    <Stack spacing={3}>
      <Typography variant="h4">{t('profesorCuenta.titulo')}</Typography>

      {/* ─── Datos personales ─── */}
      <DatosCard title={t('profesorCuenta.datosPersonales')}>
        <Grid container spacing={2} sx={{ position: 'relative', alignItems: 'flex-start' }}>
          {/* Imagen de perfil */}
          <Grid item sx={{ display: 'flex', alignItems: 'stretch' }}>
            <img
              src={gorillaprofile}
              alt="Perfil"
              style={{
                height: '100%',
                width: 'auto',
                borderRadius: '30%',
                objectFit: 'cover',
                maxHeight: 200,
              }}
            />
          </Grid>

          {/* Datos personales */}
          <Grid item xs>
            <Stack spacing={0.9}>
              <Info label={t('profesorCuenta.labels.nombre')} value={nombre} />
              <Info label={t('profesorCuenta.labels.email')} value={user.email} />
              <Info label={t('profesorCuenta.labels.fechaAlta')} value={fechaAlta} />
              <Info label={t('profesorCuenta.labels.telefono')} value={telefono} />
              <Info label={t('profesorCuenta.labels.fechaCumple')} value={fechaCumple} />
              <Info label={t('profesorCuenta.labels.roles')} value={user.roles.join(', ')} />

              <Stack direction="row" flexWrap="wrap" spacing={1}>
                {perfil.idiomas.map((i, idx) => (
                  <span
                    key={idx}
                    style={{
                      backgroundColor: '#e0e0e0',
                      borderRadius: 12,
                      padding: '4px 12px',
                      fontSize: 12,
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 500,
                    }}
                  >
                    {i.nombre}
                  </span>
                ))}
              </Stack>
            </Stack>
          </Grid>

          {/* Botón en esquina inferior derecha */}
          <Button
            variant="outlined"
            sx={{
              position: 'absolute',
              bottom: 16,
              right: 16,
              textTransform: 'uppercase',
              borderWidth: 1,
              borderColor: '#21a598',
              color: '#21a598',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'rgba(33,165,152,0.08)',
                borderColor: '#1a8578',
                boxShadow: 'none',
              },
            }}
            onClick={() => navigate('/change-password')}
          >
            {t('profesorCuenta.cambiarContrasena')}
          </Button>
        </Grid>
      </DatosCard>

      {/* Formatos + Tipos de asignatura en serie */}
      <Grid
        container
        spacing={0}
        sx={{
          marginLeft: 0,
          marginRight: 0,
          width: '100%',
          boxSizing: 'border-box',
          flexWrap: 'nowrap',
          gap: 3,
        }}
      >
        {/* Tipos de asignatura */}
        <Grid item xs={6} sx={{ display: 'flex' }}>
          <Card elevation={1} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom>
                {t('profesorCuenta.labels.tipos')}
              </Typography>
              <Stack spacing={0.5} sx={{ flex: 1 }}>
                <InfoList items={perfil.tipos.map((t) => t.descripcion ?? t.nombre)} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Formatos */}
        <Grid item xs={6} sx={{ display: 'flex' }}>
          <Card elevation={1} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom>
                {t('profesorCuenta.labels.formatos')}
              </Typography>
              <Stack spacing={0.5} sx={{ flex: 1 }}>
                <InfoList
                  items={perfil.formatos.map(
                    (f) => f.formato?.nombreFormato ?? f.nombreFormato ?? f.formato?.nombre,
                  )}
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Centros + Asignaturas */}
      <Grid
        container
        spacing={0}
        sx={{
          marginLeft: 0,
          marginRight: 0,
          width: '100%',
          boxSizing: 'border-box',
          flexWrap: 'nowrap',
          gap: 3,
        }}
      >
        {/* Centros */}
        <Grid item xs={6} sx={{ display: 'flex' }}>
          <Card elevation={1} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom>
                {t('profesorCuenta.labels.centros')}
              </Typography>

              <Stack
                direction="row"
                spacing={1.5}
                flexWrap="wrap"
                justifyContent="center"
                sx={{ mt: 1 }}
              >
                {perfil.centros.length === 0 ? (
                  <Typography variant="body2">—</Typography>
                ) : (
                  perfil.centros.map((c, i) => (
                    <Chip
                      key={i}
                      label={c.centro.nombre}
                      size="medium"
                      icon={
                        centerIconMap[c.centro.id] ? (
                          <Box
                            component="img"
                            src={centerIconMap[c.centro.id]}
                            alt=""
                            sx={{
                              width: 30,
                              height: 30,
                              objectFit: 'contain',
                            }}
                          />
                        ) : undefined
                      }
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: colorForCentro(c.centro.id),
                        color: 'black',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        height: 30,
                        px: 2.5,
                        py: 0.5,
                        borderRadius: '20px',
                        '& .MuiChip-label': {
                          display: 'flex',
                          alignItems: 'center',
                        },
                        '& .MuiChip-icon': {
                          marginLeft: '6px',
                          marginRight: '6px',
                          display: 'flex',
                          alignItems: 'center',
                        },
                      }}
                    />
                  ))
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Asignaturas */}
        <Grid item xs={6} sx={{ display: 'flex' }}>
          <Card elevation={1} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom>
                {t('profesorCuenta.labels.asignaturas')}
              </Typography>
              <Stack spacing={0.5} sx={{ flex: 1 }}>
                <InfoList items={perfil.asignaturas.map((a) => a.nombre)} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* DISPONIBILIDAD */}
      <Grid item xs={12}>
        <Card elevation={1}>
          <CardContent>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              {/* Título + Botón de cambio de disponibilidad */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6">{t('profesorCuenta.labels.disponibilidad')}</Typography>
                <AvailabilityChangeButton
                  buttonText={t('profesorCuenta.labels.solicitarCambio') ?? 'Solicitar cambio'}
                  variant="outlined"
                  color="primary"
                  size="small"
                />
              </Stack>

              {/* Switch de aceptación SAD */}
              {SAD_FEATURE_ENABLED && (
                <FormControlLabel
                  label={
                    acceptingSad
                      ? t('profesorCuenta.switch.aceptando')
                      : t('profesorCuenta.switch.noAceptando')
                  }
                  control={
                    <Switch
                      checked={acceptingSad}
                      onChange={async (_: any, checked: boolean) => {
                        const prev = acceptingSad;
                        setAcceptingSad(checked);
                        try {
                          setSavingAcceptingSad(true);
                          await api.post('/sad/accepting/mine', { accepting: checked });
                        } catch {
                          setAcceptingSad(prev);
                        } finally {
                          setSavingAcceptingSad(false);
                        }
                      }}
                      disabled={loadingAcceptingSad || savingAcceptingSad}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#21a598 !important',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#21a598 !important',
                        },
                        '& .MuiSwitch-switchBase': {
                          color: '#bdc5c6 !important',
                        },
                        '& .MuiSwitch-track': {
                          backgroundColor: '#e2e2e2 !important',
                        },
                      }}
                    />
                  }
                />
              )}
            </Stack>

            {/* Selector de vigencia */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Vigencia:
              </Typography>
              {loadingDispo ? (
                <CircularProgress size={18} />
              ) : gruposPorVigencia.size ? (
                <ToggleButtonGroup
                  size="small"
                  color="primary"
                  exclusive
                  value={vigenciaKey}
                  onChange={(_, val) => {
                    if (val) setVigenciaKey(val);
                  }}
                >
                  {Array.from(gruposPorVigencia.keys())
                    .sort((a, b) => {
                      if (a === 'base') return -1;
                      if (b === 'base') return 1;
                      const [avd] = a.split('__');
                      const [bvd] = b.split('__');
                      return avd.localeCompare(bvd);
                    })
                    .map((k) => (
                      <ToggleButton key={k} value={k}>
                        {labelForKey(k)}
                      </ToggleButton>
                    ))}
                </ToggleButtonGroup>
              ) : (
                <Typography variant="body2">—</Typography>
              )}
            </Stack>

            {/* Mosaico de disponibilidad según vigencia seleccionada */}
            <Stack spacing={1}>
              {(() => {
                if (loadingDispo) {
                  return <CircularProgress size={22} sx={{ mt: 1 }} />;
                }
                const diasKeys = Object.keys(diasMap);
                if (diasKeys.length === 0) {
                  return (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      No hay disponibilidad registrada para esta vigencia.
                    </Typography>
                  );
                }

                return diasKeys.map((dia, i) => (
                  <Grid container key={i} spacing={1} sx={{ height: 32 }} alignItems="stretch">
                    {/* Día */}
                    <Grid item xs={2} sx={{ display: 'flex' }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          backgroundColor: 'black',
                          color: 'white',
                          textAlign: 'center',
                          borderRadius: 1,
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '0 4px',
                        }}
                      >
                        {dia}
                      </Typography>
                    </Grid>

                    {/* Franjas del día */}
                    <Grid item xs={10} sx={{ display: 'flex', gap: 1, alignItems: 'stretch' }}>
                      {diasMap[dia].map((f, j) => {
                        const minWidth = 120; // un poco más para que quepa el texto
                        const maxWidth = 360;
                        const width = minWidth + (f.durHoras / durMax) * (maxWidth - minWidth);

                        return (
                          <div
                            key={j}
                            style={{
                              backgroundColor: '#1e9e85',
                              width: width,
                              borderRadius: 4,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: 12,
                              fontWeight: 'bold',
                              height: '100%',
                              padding: '0 8px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={f.etiqueta}
                          >
                            {f.etiqueta}
                          </div>
                        );
                      })}
                    </Grid>
                  </Grid>
                ));
              })()}
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      {/* cambiar contraseña */}
    </Stack>
  );
}

/* helpers UI */
function DatosCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card elevation={1}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Stack spacing={0.5}>{children}</Stack>
      </CardContent>
    </Card>
  );
}

function Info({ label, value }: { label: string; value?: string }) {
  return (
    <Typography variant="body2">
      <strong>{label}:</strong> {value ?? '—'}
    </Typography>
  );
}

function InfoList({ items }: { items: string[] }) {
  if (!items.length) return <Typography variant="body2">—</Typography>;
  return (
    <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
      {items.map((txt, i) => (
        <li key={i}>
          <Typography variant="body2">{txt}</Typography>
        </li>
      ))}
    </ul>
  );
}
